import { useState } from 'react';

import { CircleCheckIcon, CircleHelpIcon, TrendingUp } from 'lucide-react';

import { Collapsible, CollapsibleContent } from 'components/Collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { isAutoTransport } from 'lib/autoTransport/isAutoTransport';
import { useQuickQuoteCurrency } from 'pages/QuoteView/Quoting/RequestQuickQuote/hooks/useQuickQuoteCurrency';
import { titleCase } from 'utils/formatStrings';
import { getDrumkitContainer } from 'utils/getDrumkitContainer';
import { cn } from 'utils/shadcn';

import { CalculationSteps } from './components/CalculationSteps';
import { CollapsibleTriggerSection } from './components/CollapsibleTriggerSection';
import { KitReasoning } from './components/KitReasoning';
import { LowConfidenceNotice } from './components/LowConfidenceNotice';
import { SourceTable } from './components/SourceTable';
import { Suggestion } from './types';
import { buildCalculationSteps } from './utils/buildCalculationSteps';
import { buildConfidenceDisplay } from './utils/buildConfidenceDisplay';
import { formatSuggestedMargin, sourceTableRows } from './utils/format';

type InsightsCardProps = {
  suggestion: Suggestion;
  selected: boolean;
  onSelect: () => void;
  isCalculationStepsOpen?: boolean;
};

// Live Kit QQ Coach card UI (kitcoach quote from QQ v2).
export function InsightsCard({
  suggestion,
  selected,
  onSelect,
  isCalculationStepsOpen: initialIsCalculationStepsOpen,
}: InsightsCardProps) {
  const {
    serviceFeaturesEnabled: { isQuoteCalculatorMarginEnabled },
  } = useServiceFeatures();
  const { formatAmount, formatRange, formatPerMileLabel, formatPerMileRange } =
    useQuickQuoteCurrency();

  const profitPercentLabel = isQuoteCalculatorMarginEnabled
    ? 'margin'
    : 'markup';
  const targetProfitLabel = isQuoteCalculatorMarginEnabled
    ? 'Margin'
    : 'Markup';

  const distance = suggestion.distanceMiles;
  const rows = sourceTableRows(suggestion);
  const calculationSteps = buildCalculationSteps({
    suggestion,
    profitPercentLabel,
    formatMoney: formatAmount,
  });
  const confidenceDisplay = buildConfidenceDisplay(suggestion);
  const suggestedMarginLabel = formatSuggestedMargin(suggestion, formatAmount);
  const isAutoSuggestion = isAutoTransport(suggestion.transportType);
  const showKitReasoning =
    isAutoSuggestion &&
    (calculationSteps.length > 0 || Boolean(suggestion.summary?.trim()));
  const [isCalculationStepsOpen, setIsCalculationStepsOpen] = useState(
    initialIsCalculationStepsOpen ?? !isAutoSuggestion
  );
  const popoverRootContainer = getDrumkitContainer();

  return (
    <TooltipProvider>
      <div className='relative w-full'>
        <div
          data-testid='kit-coach-card'
          aria-pressed={selected}
          aria-label={`Use target buy of ${formatAmount(suggestion.targetBuy)} and suggested ${profitPercentLabel} of ${suggestedMarginLabel}`}
          className={cn(
            'w-full rounded-[4px] transition-transform',
            selected
              ? 'kit-coach-selected-ring'
              : 'cursor-pointer border-2 border-neutral-400/70 hover:scale-[1.01]'
          )}
          onClick={onSelect}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onSelect();
            }
          }}
        >
          <Flex
            direction='col'
            className={cn(
              'w-full rounded-[2px] p-2',
              selected ? 'bg-brand-50' : 'bg-neutral-50'
            )}
          >
            <Flex direction='col' className='w-full'>
              <Flex justify='between' align='center' className='w-full'>
                <Typography className='shrink-0 text-[13px]'>
                  Transport Type
                </Typography>
                <Typography variant='body-xs'>
                  {titleCase(suggestion.transportType)}
                </Typography>
              </Flex>

              <Flex justify='between' align='center' className='w-full mt-1'>
                <Typography className='shrink-0 text-[13px]'>
                  Buy Rate
                </Typography>
                <Flex
                  align='baseline'
                  className='gap-x-2 flex-wrap justify-end'
                >
                  <Typography variant='body-xs'>
                    {formatAmount(suggestion.targetBuy)}
                  </Typography>
                  {distance > 0 && (
                    <Typography variant='body-xs'>
                      {`(${formatPerMileLabel(suggestion.targetBuy, distance)})`}
                    </Typography>
                  )}
                </Flex>
              </Flex>

              <Flex justify='between' align='center' className='w-full mt-1'>
                <Typography className='shrink-0 text-[13px]'>
                  Price Range
                </Typography>
                {distance > 0 ? (
                  <Tooltip delayDuration={10}>
                    <TooltipTrigger
                      className='border-b border-dashed border-neutral-900'
                      onClick={(e) => e.preventDefault()}
                    >
                      <Typography variant='body-xs'>
                        {formatRange(suggestion.buyLow, suggestion.buyHigh)}
                      </Typography>
                    </TooltipTrigger>
                    <TooltipPortal container={popoverRootContainer}>
                      <TooltipContent className='mr-2 max-w-60 whitespace-pre-wrap'>
                        <Flex direction='col' gap='xs' className='pb-3'>
                          <Typography variant='body-xs'>
                            Price range per mile
                          </Typography>
                          <Typography variant='body-xs'>
                            {formatPerMileRange(
                              suggestion.buyLow,
                              suggestion.buyHigh,
                              distance
                            )}
                          </Typography>
                        </Flex>
                        {!isAutoSuggestion && (
                          <Typography
                            variant='body-sm'
                            className='border-t pt-3'
                          >
                            All prices include fuel.
                          </Typography>
                        )}
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                ) : (
                  <Typography variant='body-xs'>
                    {formatRange(suggestion.buyLow, suggestion.buyHigh)}
                  </Typography>
                )}
              </Flex>

              <Flex justify='between' align='center' className='w-full mt-1'>
                <Typography className='shrink-0 text-[13px]'>
                  {targetProfitLabel}
                </Typography>
                <Flex align='center' gap='sm'>
                  <TrendingUp className='h-4 w-4 shrink-0 stroke-success-600!' />
                  <Typography variant='body-xs' className='text-success-600'>
                    {suggestedMarginLabel}
                  </Typography>
                </Flex>
              </Flex>

              <Flex justify='between' align='center' className='w-full mt-1'>
                <Flex align='center' gap='xs'>
                  <Typography className='shrink-0 text-[13px]'>
                    Confidence
                  </Typography>
                  <Tooltip delayDuration={10}>
                    <TooltipTrigger
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      <CircleHelpIcon
                        className='h-3.5 w-3.5 shrink-0 text-neutral-600'
                        aria-label='How confidence is determined'
                      />
                    </TooltipTrigger>
                    <TooltipPortal container={popoverRootContainer}>
                      <TooltipContent className='ml-2 max-w-64'>
                        <Flex direction='col' gap='xs'>
                          <Typography variant='body-xs'>
                            {confidenceDisplay.tooltipIntro}
                          </Typography>
                          {confidenceDisplay.tooltipNote ? (
                            <Typography variant='body-xs'>
                              {confidenceDisplay.tooltipNote}
                            </Typography>
                          ) : null}
                          {confidenceDisplay.tooltipBands.length > 0 && (
                            <Typography
                              as='ul'
                              variant='body-xxs'
                              className='list-disc pl-4'
                            >
                              {confidenceDisplay.tooltipBands.map((band) => (
                                <Typography
                                  key={band.level}
                                  as='li'
                                  variant='body-xxs'
                                >
                                  <Typography
                                    as='span'
                                    variant='body-xxs'
                                    weight='bold'
                                  >
                                    {band.label}
                                  </Typography>
                                  {`: ${band.description}.`}
                                </Typography>
                              ))}
                            </Typography>
                          )}
                          <Typography
                            variant='body-xxs'
                            className='text-neutral-400 italic text-[10px] leading-normal pt-1'
                          >
                            {confidenceDisplay.tooltipSpread}
                          </Typography>
                        </Flex>
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </Flex>
                <Typography
                  variant='body-xs'
                  weight='medium'
                  className={confidenceDisplay.className}
                >
                  {confidenceDisplay.label}
                </Typography>
              </Flex>
            </Flex>

            {suggestion.isLowConfidence && (
              <LowConfidenceNotice sources={suggestion.sources} />
            )}

            {showKitReasoning && (
              <div
                className='mt-2 w-full'
                onClick={(event) => event.stopPropagation()}
              >
                <KitReasoning
                  summary={suggestion.summary}
                  steps={calculationSteps}
                />
              </div>
            )}

            <Collapsible
              open={isCalculationStepsOpen}
              onOpenChange={setIsCalculationStepsOpen}
              className='w-full'
            >
              <CollapsibleTriggerSection
                label={
                  showKitReasoning ? 'Rate sources' : 'How Kit calculated this'
                }
                open={isCalculationStepsOpen}
                className='mt-3 pt-3'
              />

              <CollapsibleContent>
                <Flex
                  id='kit-coach-calculation-steps'
                  direction='col'
                  gap='sm'
                  className='pb-1'
                  onClick={(event) => event.stopPropagation()}
                >
                  {!showKitReasoning && (
                    <CalculationSteps steps={calculationSteps} />
                  )}
                  <SourceTable
                    rows={rows}
                    isLowConfidenceQuote={suggestion.isLowConfidence}
                  />
                </Flex>
              </CollapsibleContent>
            </Collapsible>
          </Flex>
        </div>

        {selected && (
          <CircleCheckIcon
            className='absolute -top-3 -right-2.5 z-10 fill-neutral-50 stroke-success-500! w-6 h-6'
            aria-hidden
          />
        )}
      </div>
    </TooltipProvider>
  );
}
